import React, { useMemo, useState } from 'react';
import { User, Clock, Phone, MessageSquare, Calendar, TrendingUp } from 'lucide-react';
import { useAgentProductivityDaily } from '../hooks/useReports';
import DataTable from '../components/DataTable';
import QueryInfoButton from '../components/QueryInfoButton';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { CALL_CENTER_QUERIES } from '../constants/queries';
import { formatDate } from '../utils/formatters';

export default function AgentProductivityDaily() {
  const { data, isLoading, isError, error } = useAgentProductivityDaily();
  const [selectedDate, setSelectedDate] = useState('');

  // Get unique dates for filter
  const uniqueDates = useMemo(() => {
    if (!data?.data || data.data.length === 0) return [];
    const dates = [...new Set(data.data.map((row) => row.call_date))];
    return dates.sort((a, b) => new Date(b) - new Date(a));
  }, [data]);

  // Filter data by selected date
  const filteredData = useMemo(() => {
    if (!data?.data) return [];
    if (!selectedDate) return data.data;
    return data.data.filter((row) => row.call_date === selectedDate);
  }, [data, selectedDate]);

  // Chart data - aggregate by agent for selected date or top 10 agents overall
  const chartData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];

    // Aggregate by agent
    const agentMap = {};
    filteredData.forEach((row) => {
      const agentId = row.agent_id;
      if (!agentMap[agentId]) {
        agentMap[agentId] = {
          agentName: row.agent_name || `Agent ${agentId}`,
          interactions: 0,
          talkMinutes: 0,
          wrapMinutes: 0,
          acwMinutes: 0,
        };
      }
      agentMap[agentId].interactions += parseInt(row.interactions_handled) || 0;
      agentMap[agentId].talkMinutes += parseFloat(row.talk_minutes) || 0;
      agentMap[agentId].wrapMinutes += parseFloat(row.wrap_minutes) || 0;
      agentMap[agentId].acwMinutes += parseFloat(row.acw_minutes) || 0;
    });

    return Object.values(agentMap)
      .sort((a, b) => b.interactions - a.interactions)
      .slice(0, 10)
      .map((agent) => ({
        ...agent,
        talkMinutes: parseFloat(agent.talkMinutes.toFixed(1)),
        wrapMinutes: parseFloat(agent.wrapMinutes.toFixed(1)),
        acwMinutes: parseFloat(agent.acwMinutes.toFixed(1)),
      }));
  }, [filteredData]);

  // Summary stats
  const summaryStats = useMemo(() => {
    if (!filteredData || filteredData.length === 0) {
      return {
        totalInteractions: 0,
        totalTalkMinutes: 0,
        totalWrapMinutes: 0,
        totalAcwMinutes: 0,
        uniqueAgents: 0,
      };
    }

    const uniqueAgents = new Set(filteredData.map((row) => row.agent_id)).size;
    const totals = filteredData.reduce(
      (acc, row) => ({
        totalInteractions: acc.totalInteractions + (parseInt(row.interactions_handled) || 0),
        totalTalkMinutes: acc.totalTalkMinutes + (parseFloat(row.talk_minutes) || 0),
        totalWrapMinutes: acc.totalWrapMinutes + (parseFloat(row.wrap_minutes) || 0),
        totalAcwMinutes: acc.totalAcwMinutes + (parseFloat(row.acw_minutes) || 0),
      }),
      { totalInteractions: 0, totalTalkMinutes: 0, totalWrapMinutes: 0, totalAcwMinutes: 0 }
    );

    return {
      ...totals,
      uniqueAgents,
    };
  }, [filteredData]);

  const columns = [
    {
      key: 'call_date',
      label: 'Date',
      sortable: true,
      render: (value) => formatDate(value),
    },
    { key: 'agent_id', label: 'Agent ID', sortable: true },
    { key: 'agent_name', label: 'Agent Name', sortable: true },
    { key: 'interactions_handled', label: 'Interactions', sortable: true, format: 'number' },
    { key: 'talk_minutes', label: 'Talk (min)', sortable: true, format: 'decimal' },
    { key: 'wrap_minutes', label: 'Wrap (min)', sortable: true, format: 'decimal' },
    { key: 'acw_minutes', label: 'ACW (min)', sortable: true, format: 'decimal' },
  ];

  const StatCard = ({ title, value, icon: Icon, subtitle }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="p-2 bg-primary-50 rounded-lg">
          <Icon className="w-5 h-5 text-primary-600" />
        </div>
      </div>
      <div className="mt-3">
        <p className="text-sm text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Agent Productivity (Daily)</h2>
          <p className="text-gray-500 mt-1">
            Daily breakdown of agent performance metrics including talk time, wrap time, and ACW
          </p>
        </div>
        <div className="flex items-center gap-4">
          <QueryInfoButton
            title="Agent Productivity Data Sources"
            queries={[CALL_CENTER_QUERIES.agentProductivityDaily]}
          />
          <div className="flex items-center gap-2">
            <label htmlFor="dateFilter" className="text-sm font-medium text-gray-700">
              Filter by Date:
            </label>
            <select
              id="dateFilter"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Dates</option>
              {uniqueDates.map((date) => (
                <option key={date} value={date}>
                  {formatDate(date)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          title="Active Agents"
          value={summaryStats.uniqueAgents.toLocaleString()}
          icon={User}
          subtitle={selectedDate ? 'on selected date' : 'total unique'}
        />
        <StatCard
          title="Total Interactions"
          value={summaryStats.totalInteractions.toLocaleString()}
          icon={Phone}
          subtitle="handled by agents"
        />
        <StatCard
          title="Total Talk Time"
          value={`${summaryStats.totalTalkMinutes.toFixed(1)} min`}
          icon={MessageSquare}
          subtitle="across all agents"
        />
        <StatCard
          title="Total Wrap Time"
          value={`${summaryStats.totalWrapMinutes.toFixed(1)} min`}
          icon={Clock}
          subtitle="post-call work"
        />
        <StatCard
          title="Total ACW Time"
          value={`${summaryStats.totalAcwMinutes.toFixed(1)} min`}
          icon={Clock}
          subtitle="after conversation work"
        />
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="card">
          <h3 className="card-header flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary-600" />
            Top 10 Agents by Interactions {selectedDate && `(${formatDate(selectedDate)})`}
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="agentName"
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis tick={{ fontSize: 12 }} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                  }}
                />
                <Legend />
                <Bar dataKey="interactions" name="Interactions" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Time Distribution Chart */}
      {chartData.length > 0 && (
        <div className="card">
          <h3 className="card-header flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary-600" />
            Time Distribution by Agent (Minutes)
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="agentName"
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis tick={{ fontSize: 12 }} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                  }}
                />
                <Legend />
                <Bar dataKey="talkMinutes" name="Talk Time" fill="#22c55e" stackId="time" />
                <Bar dataKey="wrapMinutes" name="Wrap Time" fill="#f59e0b" stackId="time" />
                <Bar dataKey="acwMinutes" name="ACW Time" fill="#8b5cf6" stackId="time" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="card">
        <h3 className="card-header flex items-center gap-2">
          <Calendar className="w-5 h-5 text-primary-600" />
          Agent Productivity Data
        </h3>
        <DataTable
          data={filteredData}
          columns={columns}
          loading={isLoading}
          emptyMessage="No agent productivity data available"
        />
      </div>
    </div>
  );
}
